import React from 'react';
import { Mail, FileText, Lock, Users, ShieldCheck, ScrollText, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageSEO } from '../PageSEO';
import { SiteJsonLd } from '../institutional/SiteJsonLd';
import { InstitutionalNavbar } from '../institutional/InstitutionalNavbar';
import { InstitutionalFooter } from '../institutional/InstitutionalFooter';
import { ENTITY } from '../../lib/entity';

/**
 * Phase 99A.1 — /security in the public light direction.
 *
 * Layout: navbar → light hero (paper, editorial Fraunces heading, italic
 * gold accent) → factual content sections in light panel cards →
 * institutional footer.
 *
 * Factual content is preserved verbatim from the previous Phase 71 page.
 * No SOC 2 / ISO certifications claimed. No bank-grade / military-grade /
 * unhackable / guaranteed-secure / 99.9% uptime language.
 */

const ControlCard = ({ icon: Icon, title, body, bullets, testId }) => (
  <article
    className="rounded-xl p-6 lg:p-7"
    style={{ background: 'var(--gpc-panel)', border: '1px solid var(--gpc-line)' }}
    data-testid={testId}
  >
    <div className="flex items-center gap-3 mb-3">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: 'rgba(140,107,46,0.10)', border: '1px solid rgba(140,107,46,0.18)' }}
      >
        <Icon className="w-4 h-4" style={{ color: 'var(--gpc-ink-soft)' }} />
      </div>
      <h3
        style={{
          fontFamily: 'Space Grotesk, system-ui, sans-serif',
          fontWeight: 600,
          fontSize: '1.05rem',
          color: 'var(--gpc-ink)',
        }}
      >
        {title}
      </h3>
    </div>
    {body && (
      <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '14px', lineHeight: 1.6, color: 'var(--gpc-ink-soft)' }}>
        {body}
      </p>
    )}
    {bullets && (
      <ul className="mt-3 space-y-1.5" style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '14px', color: 'var(--gpc-ink-soft)' }}>
        {bullets.map((b, i) => (
          <li key={i} className="flex gap-2">
            <span aria-hidden="true" style={{ color: 'var(--gpc-ink-soft)', flexShrink: 0 }}>·</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    )}
  </article>
);

export const Security = () => (
  <div className="min-h-screen" style={{ background: 'var(--gpc-paper)' }} data-testid="security-page">
    <PageSEO
      title="Security & Responsible Disclosure — GP Capital"
      description="Responsible-disclosure contact and a factual reference to GP Capital's permissioned platform-access and document-handling model. Private Capital is for wholesale / sophisticated counterparties only."
      path="/security"
      referrerPolicy="strict-origin"
    />
    <SiteJsonLd page="security" />
    <InstitutionalNavbar />

    <div className="pt-20">
      {/* Light hero */}
      <section style={{ background: 'var(--gpc-paper)', borderBottom: '1px solid var(--gpc-line-soft)' }} data-testid="security-hero">
        <div className="max-w-5xl mx-auto px-6 lg:px-8" style={{ padding: 'clamp(3rem, 6vw, 5rem) clamp(1.5rem, 4vw, 4rem)' }}>
          <p className="gpc-eyebrow" style={{ marginBottom: '14px' }}>Security</p>
          <h1
            data-testid="security-page-h1"
            style={{
              fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
              fontWeight: 400,
              fontSize: 'clamp(2.25rem, 4.4vw, 3.4rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.015em',
              color: 'var(--gpc-ink)',
              marginBottom: '14px',
            }}
          >
            Controlled access. Permissioned workflows.{' '}
            <span style={{ color: 'var(--gpc-ink-soft)', fontStyle: 'italic' }}>Audit-aware execution.</span>
          </h1>
          <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '1.075rem', color: 'var(--gpc-ink-soft)', maxWidth: '40rem', lineHeight: 1.6 }}>
            Reference information for procurement, compliance and responsible-disclosure review. The Services described here are operated by{' '}
            <strong style={{ color: 'var(--gpc-ink)' }}>{ENTITY.legalName}</strong> (ACN {ENTITY.acn} · ABN {ENTITY.abn}).
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16" style={{ background: 'var(--gpc-paper)' }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-5">
            <ControlCard
              icon={Users}
              title="Role-based access"
              testId="security-card-rbac"
              body="Access to the GP Capital deal-room platform is invitation-only and permissioned. Counterparty access is granted on a per-deal basis according to role and explicit assignment — not by counterparty type alone."
              bullets={[
                'External access is controlled by the GP Capital internal team.',
                'Document visibility is governed by role and per-document permissions.',
                'Access may be restricted, time-limited, or revoked by the internal team.',
              ]}
            />
            <ControlCard
              icon={Lock}
              title="NDA-gated document rooms"
              testId="security-card-nda"
              body="Materials shared via GP Capital deal rooms are confidential and intended only for the recipient. Counterparties are granted access according to their role and the relevant mandate; access does not constitute an offer of securities."
              bullets={[
                'NDA acceptance is required before counterparties can view deal materials.',
                'External redistribution requires GP Capital\u2019s prior written consent.',
              ]}
            />
            <ControlCard
              icon={FileText}
              title="Controlled preview & download"
              testId="security-card-preview"
              body="Where applicable, documents may be subject to preview-only controls, download controls, or watermarking — applied per-document at the discretion of the internal team. We do not claim every document is watermarked or download-restricted by default; controls are applied where the underlying counterparty or transaction requires them."
            />
            <ControlCard
              icon={ScrollText}
              title="Audit logs"
              testId="security-card-audit"
              body="Document access events are logged for audit purposes. Permission changes and administrative actions are recorded. Material logs are retained consistent with our legal and regulatory obligations."
            />
            <ControlCard
              icon={ShieldCheck}
              title="MFA & passkeys"
              testId="security-card-mfa"
              body="Internal accounts use multi-factor authentication; TOTP is required for internal users and operations roles. Passkey / passwordless sign-in is supported for internal users."
            />
            <ControlCard
              icon={Mail}
              title="Security contact"
              testId="security-card-contact"
              bullets={[
                <>Email <a href={`mailto:${ENTITY.contact.general}?subject=${encodeURIComponent('Security report')}`} className="underline" style={{ color: 'var(--gpc-ink-soft)' }} data-testid="security-contact-email">{ENTITY.contact.general}</a></>,
                <>Machine-readable record at <a href="/.well-known/security.txt" className="underline font-mono" style={{ color: 'var(--gpc-ink-soft)' }} data-testid="security-well-known-link">/.well-known/security.txt</a> (RFC 9116)</>,
                'We do not currently operate a paid bug-bounty programme.',
              ]}
            />
          </div>

          {/* Related links */}
          <div
            className="mt-12 p-6 rounded-xl"
            style={{ background: 'var(--gpc-panel)', border: '1px solid var(--gpc-line)' }}
            data-testid="security-related"
          >
            <p className="gpc-eyebrow" style={{ marginBottom: '10px' }}>Related policies</p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5" style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '14px' }}>
              <Link to="/trust" className="related-link" data-testid="security-link-trust-center">Trust Center →</Link>
              <Link to="/privacy" className="related-link" data-testid="security-link-privacy">Privacy Policy →</Link>
              <Link to="/terms" className="related-link" data-testid="security-link-terms">Terms of Service →</Link>
              <Link to="/platform-terms" className="related-link" data-testid="security-link-platform-terms">Platform Terms →</Link>
              <Link to="/disclosures" className="related-link" data-testid="security-link-disclosures">Disclosures →</Link>
              <a href="/.well-known/security.txt" className="related-link font-mono" data-testid="security-link-security-txt">/.well-known/security.txt →</a>
            </div>
          </div>

          {/* Disclaimer */}
          <p
            className="mt-12"
            style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '12px', color: 'var(--gpc-ink-soft)', maxWidth: '60rem' }}
            data-testid="security-page-disclaimer"
          >
            This page is provided for informational purposes only and does not form part of any contract. It is intended for wholesale / sophisticated counterparties. Nothing on this page constitutes an offer of securities or a representation regarding any specific transaction.
          </p>
          <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '11px', color: 'var(--gpc-ink-soft)', marginTop: '8px' }}>
            Last updated: 12 May 2026.
          </p>

          {/* CTA */}
          <div className="mt-10 flex flex-wrap gap-3" data-testid="security-cta-row">
            <Link to="/trust" className="gpc-cta-primary" data-testid="security-cta-trust">
              Open the Trust Center
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="gpc-cta-secondary" data-testid="security-cta-contact">
              Contact the team
            </Link>
          </div>
        </div>
      </section>
    </div>

    <InstitutionalFooter />

    <style>{`
      .related-link {
        color: var(--gpc-ink);
        text-decoration: none;
        transition: color 0.2s ease;
      }
      .related-link:hover { color: var(--gpc-gold); }
    `}</style>
  </div>
);

export default Security;
